import Alert from '@mui/material/Alert';
import {
  CategoryScale,
  Chart,
  ChartData,
  ChartDataset,
  ChartOptions,
  LinearScale,
  LineElement,
  registerables,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-moment';
import zoomPlugin from 'chartjs-plugin-zoom';
import * as React from 'react';
import { Line } from 'react-chartjs-2';
import { ChargePoint, ResponseListChargePointAuditLogs } from '../../../../../../../app/ApiGen';
import { ResetZoomButton } from '../../../../../../../components/ResetZoomButton';
import { Section } from '../../../../../../../design-system';
import { ChargePointLogs } from '../../../../../../../utils/ChargePointLogs';
import { updateChartZoomLocations } from '../../../../../../../utils/chart';
import { NormalisedWithRelationships } from '../../../../../../../utils/request';
import { MeteringChartModel } from '../utils/MeteringChartModel';
import { CHART_DATE_FORMAT, diagnosticsChartIds, DiagnosticsChartType } from '../utils/meteringCharts';
import { AmberTool } from './AmberTool';

type Props = {
  data: ChargePointLogs;
  startingDate: string;
  endDate: string;
  amberLogs: ResponseListChargePointAuditLogs | null;
  chargePoint: NormalisedWithRelationships<ChargePoint>;
};

Chart.register(LineElement, zoomPlugin, CategoryScale, LinearScale, TimeScale, ...registerables);

const chartOptions = (startingDate: string, endDate: string): ChartOptions<'line'> => ({
  scales: {
    x: { type: 'time', min: startingDate, max: endDate, time: { tooltipFormat: CHART_DATE_FORMAT } },
    y: {
      afterFit(scale) {
        // eslint-disable-next-line no-param-reassign -- -- Reassigning the `scale` param's properties is the accepted way to set dimensions in ChartJS
        scale.width = 100;
      },
      ticks: { crossAlign: 'near' },
      afterSetDimensions: (scale) => {
        // eslint-disable-next-line no-param-reassign -- Reassigning the `scale` param's properties is the accepted way to set dimensions in ChartJS
        scale.maxWidth = 100;
      },
    },
  },
  animation: false,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: 'nearest', axis: 'x' },
  plugins: {
    legend: { position: 'bottom' },
    zoom: {
      pan: { enabled: true, modifierKey: 'ctrl', mode: 'x' },
      zoom: {
        onZoomComplete: (selectedChart) =>
          updateChartZoomLocations<DiagnosticsChartType>(selectedChart, diagnosticsChartIds),
        mode: 'x',
        drag: { enabled: true },
      },
    },
  },
});

const chartData: (data: ChartDataset<'line'>[]) => ChartData<'line'> = (data) => ({ datasets: data });

const AmberIntegrationSection: React.FC<Omit<Props, 'data'>> = ({ startingDate, endDate, amberLogs, chargePoint }) => {
  const hasAmberIntegration = !!chargePoint?.integrations?.amberElectric;

  if (!amberLogs || amberLogs.data.length === 0) {
    if (hasAmberIntegration) {
      return (
        <Section style={{ paddingBottom: 64 }}>
          <Alert severity="warning">No Amber data</Alert>
        </Section>
      );
    }
    return null;
  }

  return (
    <Section style={{ paddingBottom: 64 }}>
      <ResetZoomButton<DiagnosticsChartType>
        chartIds={diagnosticsChartIds}
        minXValue={startingDate}
        maxXValue={endDate}
      />
      <AmberTool data={amberLogs.data} startDate={new Date(startingDate)} endDate={new Date(endDate)} />
    </Section>
  );
};

export const MeteringCharts: React.FC<Props> = ({ data, startingDate, endDate, amberLogs, chargePoint }) => {
  const currentResponses = new MeteringChartModel({ logs: data, measurand: 'Current.Import' }).toChartData();
  const currentOfferedResponses = new MeteringChartModel({ logs: data, measurand: 'Current.Offered' }).toChartData();
  const voltageResponses = new MeteringChartModel({ logs: data, measurand: 'Voltage' }).toChartData();
  const powerResponses = new MeteringChartModel({ logs: data, measurand: 'Power.Active.Import' }).toChartData();
  const temperatureResponses = new MeteringChartModel({ logs: data, measurand: 'Temperature' }).toChartData();

  const options = chartOptions(startingDate, endDate);

  return (
    <>
      <ResetZoomButton<DiagnosticsChartType>
        chartIds={diagnosticsChartIds}
        minXValue={startingDate}
        maxXValue={endDate}
      />
      <Section style={{ height: '50vh', position: 'relative', paddingBottom: 64 }}>
        <Line id={diagnosticsChartIds.power} options={options} data={chartData(powerResponses)} />
      </Section>

      <ResetZoomButton<DiagnosticsChartType>
        chartIds={diagnosticsChartIds}
        minXValue={startingDate}
        maxXValue={endDate}
      />
      <Section style={{ height: '50vh', position: 'relative', paddingBottom: 64 }}>
        <Line
          id={diagnosticsChartIds.current}
          options={options}
          data={{ datasets: [...currentResponses, ...currentOfferedResponses] }}
        />
      </Section>

      <AmberIntegrationSection
        startingDate={startingDate}
        endDate={endDate}
        amberLogs={amberLogs}
        chargePoint={chargePoint}
      />

      <ResetZoomButton<DiagnosticsChartType>
        chartIds={diagnosticsChartIds}
        minXValue={startingDate}
        maxXValue={endDate}
      />
      <Section style={{ height: '25vh', position: 'relative', paddingBottom: 64 }}>
        <Line id={diagnosticsChartIds.voltage} options={options} data={chartData(voltageResponses)} />
      </Section>

      <ResetZoomButton<DiagnosticsChartType>
        chartIds={diagnosticsChartIds}
        minXValue={startingDate}
        maxXValue={endDate}
      />
      <Section style={{ height: '25vh', position: 'relative', paddingBottom: 64 }}>
        <Line id={diagnosticsChartIds.temperature} options={options} data={chartData(temperatureResponses)} />
      </Section>
    </>
  );
};
